/* Independently implemented showcase navigation and optional display-only countdown. */
(() => {
  if (window.__rizonProductShowcase) return;
  window.__rizonProductShowcase = true;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const galleries = new Map();
  const productEntries = new Map();
  const showcaseSelector = '.main-product--gallery-showcase [data-gallery-showcase]';
  const productSelector = '.main-product [data-product-root]';
  function enhanceProductEntry(productRoot) {
    if (productEntries.has(productRoot)) return;
    const images = [...productRoot.querySelectorAll('[data-product-entry-media]')];
    if (!images.length || reduced() || !document.body.classList.contains('animations-enabled')) return;
    productRoot.setAttribute('data-product-entry-active', '');
    let disposed = false;
    const cleanups = images.map(image => {
      let source = image.currentSrc || image.src;
      const reveal = () => {
        if (disposed || !image.isConnected) return;
        image.setAttribute('data-product-entry-ready', '');
      };
      const ready = event => {
        if (event?.type === 'error' || !image.naturalWidth || typeof image.decode !== 'function') { reveal(); return; }
        source = image.currentSrc || image.src;
        image.decode().catch(() => {}).then(() => {
          if (source === (image.currentSrc || image.src)) reveal();
          else if (image.complete) ready();
        });
      };
      image.addEventListener('load', ready);
      image.addEventListener('error', ready);
      if (image.complete) ready();
      return () => { image.removeEventListener('load', ready); image.removeEventListener('error', ready); };
    });
    productEntries.set(productRoot, () => {
      disposed = true;
      cleanups.forEach(cleanup => cleanup());
      productRoot.removeAttribute('data-product-entry-active');
      images.forEach(image => image.removeAttribute('data-product-entry-ready'));
      productEntries.delete(productRoot);
    });
  }
  function enhanceGallery(gallery) {
    if (galleries.has(gallery)) return;
    const main = gallery.querySelector('[data-gallery-main]');
    const rail = gallery.querySelector('[data-gallery-thumbs]');
    const items = [...main?.querySelectorAll('[data-media-id]') || []];
    const thumbs = [...rail?.querySelectorAll('[data-media-target]') || []];
    if (!main || !items.length || !thumbs.length) return;
    const controls = gallery.querySelector('[data-showcase-controls]');
    let frame, activeIndex = 0;
    const horizontal = () => getComputedStyle(main).gridAutoFlow.includes('column');
    const mark = index => {
      activeIndex = index;
      const id = items[index]?.dataset.mediaId;
      if (controls) {
        controls.hidden = !horizontal() || items.length < 2;
        controls.querySelector('[data-gallery-step="-1"]').disabled = index === 0;
        controls.querySelector('[data-gallery-step="1"]').disabled = index === items.length - 1;
      }
      for (const thumb of thumbs) {
        const active = thumb.dataset.mediaTarget === id;
        thumb.classList.toggle('media-gallery__thumb--active', active);
        thumb.setAttribute('aria-current', String(active));
      }
    };
    const sync = () => {
      frame = null;
      const isHorizontal = horizontal();
      const start = isHorizontal ? main.getBoundingClientRect().left : 30;
      const coordinate = item => isHorizontal ? item.getBoundingClientRect().left : item.getBoundingClientRect().top;
      let closest = 0;
      items.forEach((item, index) => { if (Math.abs(coordinate(item) - start) < Math.abs(coordinate(items[closest]) - start)) closest = index; });
      mark(closest);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
    const choose = thumb => {
      const index = items.findIndex(item => item.dataset.mediaId === thumb.dataset.mediaTarget);
      if (index < 0) return;
      const item = items[index], behavior = reduced() ? 'instant' : 'smooth';
      if (horizontal()) main.scrollTo({ left: main.scrollLeft + item.getBoundingClientRect().left - main.getBoundingClientRect().left, behavior });
      else window.scrollTo({ top: window.scrollY + item.getBoundingClientRect().top - 30, behavior });
      mark(index);
    };
    const click = event => {
      const arrow = event.target.closest('[data-gallery-step]');
      if (arrow && controls?.contains(arrow)) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!arrow.disabled) choose(thumbs[Math.max(0,Math.min(thumbs.length - 1,activeIndex + Number(arrow.dataset.galleryStep)))]);
        return;
      }
      const thumb = event.target.closest('[data-media-target]');
      if (!thumb || !rail.contains(thumb)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      choose(thumb); // Keep horizontal selection from moving the entire document.
    };
    const key = event => {
      const index = thumbs.indexOf(event.target.closest('[data-media-target]'));
      if (index < 0 || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1 : Math.max(0, Math.min(thumbs.length - 1, index + (['ArrowLeft','ArrowUp'].includes(event.key) ? -1 : 1)));
      thumbs[next].focus({ preventScroll: true }); choose(thumbs[next]);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(main);
    main.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    gallery.addEventListener('click', click, true); rail.addEventListener('keydown', key);
    gallery.addEventListener('load', schedule, true);
    sync();
    galleries.set(gallery, () => {
      cancelAnimationFrame(frame); observer.disconnect();
      main.removeEventListener('scroll', schedule); window.removeEventListener('scroll', schedule);
      gallery.removeEventListener('click', click, true); rail.removeEventListener('keydown', key);
      gallery.removeEventListener('load', schedule, true);
      thumbs.forEach(thumb => thumb.removeAttribute('aria-current'));
      if (controls) controls.hidden = true;
      galleries.delete(gallery);
    });
  }
  const mount = root => {
    if (root.matches?.(showcaseSelector)) enhanceGallery(root);
    root.querySelectorAll(showcaseSelector).forEach(enhanceGallery);
    if (root.matches?.(productSelector)) enhanceProductEntry(root);
    root.querySelectorAll(productSelector).forEach(enhanceProductEntry);
  };
  const unmount = root => {
    for (const [gallery, cleanup] of galleries) if (root.contains(gallery)) cleanup();
    for (const [productRoot, cleanup] of productEntries) if (root.contains(productRoot)) cleanup();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true }); else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  new MutationObserver(records => {
    for (const [gallery, cleanup] of galleries) if (!gallery.isConnected) cleanup();
    for (const [productRoot, cleanup] of productEntries) if (!productRoot.isConnected) cleanup();
    for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) mount(node);
  }).observe(document.documentElement, { childList: true, subtree: true });

})();
